import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Badge
} from '@mui/material';
import { Archive, Undo, Image as ImageIcon } from 'lucide-react';
import { Performance } from '../types/mongodb';
import { getStatusColor } from '../utils/performanceUtils';
import { isStrand, isBandRecommendation } from '../utils/typeGuards';
import axiosInstance from '../utils/axiosConfig'; // Adjust import path as needed

interface ExtendedPerformance extends Omit<Performance, 'isTicketed'> {
  isTicketed: boolean; // Make it required, not optional
  titoData?: {
    totalSold?: number;
    totalRevenue?: number;
    status?: 'on_sale' | 'not_on_sale' | 'not_ticketed' | 'error'; // Add 'not_ticketed'
    message?: string;
    releases?: Array<{
      title: string;
      quantity: number;
      tickets_count: number;
      price: number;
    }>;
  };
  // Add venue as any type to match your actual data structure
  venue?: any;
}

interface MarketingAsset {
  filename: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
  description?: string;
  uploadedAt: Date;
}

interface ResourceData {
  _id: string;
  marketingAssets?: MarketingAsset[];
  mainImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
  };
  mainPosterImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    dimensions?: string;
    format?: string;
  };
  additionalPromoImages?: Array<{
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    description?: string;
  }>;
}

interface PerformanceCardProps {
  performance: ExtendedPerformance;
  onArchive: (performance: ExtendedPerformance) => void;
  onUnarchive: (performance: ExtendedPerformance) => void;
}

const PerformanceCard: React.FC<PerformanceCardProps> = ({
  performance,
  onArchive,
  onUnarchive
}) => {
  const navigate = useNavigate();
  const [resourceData, setResourceData] = useState<ResourceData | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [hasMarketingAssets, setHasMarketingAssets] = useState(false);
  const [loadingResource, setLoadingResource] = useState(false);
  
  // Calculate days remaining until the event
  const now = new Date();
  const startDate = new Date(performance.startTime);
  const daysRemaining = Math.ceil((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // Fetch resource data for this performance
  useEffect(() => {
    const fetchResourceData = async () => {
      if (!performance._id) return;
      
      setLoadingResource(true);
      try {
        // Use the existing endpoint from your resources routes
        const response = await axiosInstance.get(`/api/resources/performance/${performance._id}`);
        
        if (response.data && response.data.exists !== false) {
          // Only process if resource actually exists (not just a template)
          const resource = response.data;
          setResourceData(resource);
          
          // Check for marketing images and set background
          const marketingImage = getMarketingImage(resource);
          setBackgroundImage(marketingImage);
          
          // Check if performance has any marketing assets
          const hasAssets = checkHasMarketingAssets(resource);
          setHasMarketingAssets(hasAssets);
        }
      } catch (error) {
        // Resource might not exist yet - that's okay
        console.log('No resource found for performance:', performance._id);
      } finally {
        setLoadingResource(false);
      }
    };

    fetchResourceData();
  }, [performance._id]);

  // Helper function to get the best marketing image for background
  const getMarketingImage = (resource: ResourceData): string | null => {
    let imageUrl: string | null = null;

    // Priority 1: Marketing assets with type 'image'
    if (resource.marketingAssets && resource.marketingAssets.length > 0) {
      const imageAsset = resource.marketingAssets.find(asset => asset.type === 'image');
      if (imageAsset) {
        imageUrl = imageAsset.url;
      }
    }

    // Priority 2: Main poster image
    if (!imageUrl && resource.mainPosterImage?.url) {
      imageUrl = resource.mainPosterImage.url;
    }

    // Priority 3: Main image
    if (!imageUrl && resource.mainImage?.url) {
      imageUrl = resource.mainImage.url;
    }

    // Priority 4: First additional promo image
    if (!imageUrl && resource.additionalPromoImages && resource.additionalPromoImages.length > 0) {
      imageUrl = resource.additionalPromoImages[0].url;
    }

    // URL encode the image URL to handle spaces and special characters
    if (imageUrl) {
      // Split the URL to only encode the filename part after the last slash
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      const encodedFilename = encodeURIComponent(filename);
      urlParts[urlParts.length - 1] = encodedFilename;
      return urlParts.join('/');
    }

    return null;
  };

  // Helper function to check if performance has marketing assets
  const checkHasMarketingAssets = (resource: ResourceData): boolean => {
    return !!(
      (resource.marketingAssets && resource.marketingAssets.length > 0) ||
      resource.mainImage?.url ||
      resource.mainPosterImage?.url ||
      (resource.additionalPromoImages && resource.additionalPromoImages.length > 0)
    );
  };

  // Helper functions for card display data
  const getBandName = () => {
    if (performance.simpleBandName) return performance.simpleBandName;
    if (!performance.band) return 'Unknown Artist';
    return isBandRecommendation(performance.band) ? performance.band.artistName : 'Unknown Artist';
  };

  const getStrandName = () => {
    if (!performance.strandId) return null;
    return isStrand(performance.strandId) ? performance.strandId.name : null;
  };
  
  // Get venue name with proper handling for direct venue and event venue
  const getVenueName = () => {
    // First check if there's a direct venue reference
    if (performance.venue) {
      // If venue is an object with a name property
      if (typeof performance.venue === 'object' && performance.venue !== null && 'name' in performance.venue) {
        return performance.venue.name;
      }
    }
    
    // Then check if venue is accessed through event
    if (performance.event) {
      const event = performance.event;
      // If event is an object with a venue property
      if (typeof event === 'object' && event !== null && event.venue) {
        const venue = event.venue;
        // If venue is an object with a name property
        if (typeof venue === 'object' && venue !== null && 'name' in venue) {
          return venue.name;
        }
      }
    }
    
    return 'TBA';
  };

  // Get venue capacity with proper handling for direct venue and event venue
  const getVenueCapacity = () => {
    // First check if there's a direct venue reference
    if (performance.venue) {
      // If venue is an object with a capacity property
      if (typeof performance.venue === 'object' && performance.venue !== null && 'capacity' in performance.venue) {
        return performance.venue.capacity || 0;
      }
    }
    
    // Then check if venue is accessed through event
    if (performance.event) {
      const event = performance.event;
      // If event is an object with a venue property
      if (typeof event === 'object' && event !== null && event.venue) {
        const venue = event.venue;
        // If venue is an object with a capacity property
        if (typeof venue === 'object' && venue !== null && 'capacity' in venue) {
          return venue.capacity || 0;
        }
      }
    }
    
    return performance.revenue?.expectedCapacity || 0;
  };
  
  // Get ticket sales data with proper error handling
  const getTicketSalesData = () => {
    // Get capacity from venue or fallback to expected
    const ticketCapacity = getVenueCapacity();
    
    // Default values when no data is available
    const defaultData = {
      ticketsSold: 0,
      ticketCapacity,
      ticketSalesPercentage: 0,
      totalRevenue: 0,
      status: 'unknown'
    };
    
    // If we have no Tito data at all
    if (!performance.titoData) {
      return {
        ...defaultData,
        // Fallback to expected occupancy if we have it
        ticketsSold: performance.revenue?.expectedOccupancy || 0,
        ticketSalesPercentage: performance.revenue?.expectedOccupancy && ticketCapacity
          ? (performance.revenue.expectedOccupancy / ticketCapacity) * 100 
          : 0
      };
    }
    
    // If Tito data has a "not_on_sale" status
    if (performance.titoData.status === 'not_on_sale') {
      return {
        ...defaultData,
        status: 'not_on_sale'
      };
    }
    
    // If Tito data has an error status
    if (performance.titoData.status === 'error') {
      return {
        ...defaultData,
        status: 'error'
      };
    }
    
    // Use actual Tito data when available
    const ticketsSold = performance.titoData.totalSold || 0;
    return {
      ticketsSold,
      ticketCapacity,
      ticketSalesPercentage: ticketCapacity > 0 ? (ticketsSold / ticketCapacity) * 100 : 0,
      totalRevenue: performance.titoData.totalRevenue || 0,
      status: 'on_sale'
    };
  };

  // Get ticket data
  const salesData = getTicketSalesData();
  const { 
    ticketsSold, 
    ticketCapacity, 
    ticketSalesPercentage, 
    totalRevenue, 
    status 
  } = salesData;

  // Determine color based on sales percentage
  const getSalesColor = () => {
    // Don't show any color if not ticketed
    if (performance.isTicketed === false || performance.titoData?.status === 'not_ticketed') {
      return 'transparent';
    }
    
    if (status === 'not_on_sale') return 'error.main';
    
    if (ticketSalesPercentage >= 60) return 'success.main';
    if (ticketSalesPercentage >= 25) return 'info.main';
    return 'warning.main';
  };

  // Calculate staff assignment status
  const staffCount = performance.staff?.length || 0;
  const staffNeeded = 3; // This could be dynamic based on event type/venue

  // Handle card click - navigate to details
  const handleCardClick = () => {
    navigate(`/admin/performances/${performance._id}`);
  };
  
  // Handle archive button click with stopPropagation to prevent card click
  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (performance.isArchived) {
      onUnarchive(performance);
    } else {
      onArchive(performance);
    }
  };

  // Render ticket sales section with proper handling for not-on-sale case
  const renderTicketSales = () => {
    // Don't show ticket sales at all if the event is not ticketed
    if (performance.isTicketed === false || performance.titoData?.status === 'not_ticketed') {
      return null; // Don't render anything
    }

    if (status === 'not_on_sale') {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Tickets: Not yet on sale
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={0} 
            sx={{ 
              mt: 0.5, 
              height: 6, 
              borderRadius: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.1)'
            }} 
          />
        </Box>
      );
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Ticket Sales: {ticketsSold}/{ticketCapacity} ({Math.round(ticketSalesPercentage)}%)
          {totalRevenue > 0 && 
            ` • €${totalRevenue.toFixed(2)}`
          }
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={ticketSalesPercentage} 
          sx={{ 
            mt: 0.5, 
            height: 6, 
            borderRadius: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getSalesColor()
            }
          }} 
        />
      </Box>
    );
  };

  return (
    <Card 
      sx={{ 
        position: 'relative',
        borderTop: 3,
        borderColor: getSalesColor(),
        transition: 'all 0.3s ease',
        overflow: 'hidden', // Important for background image
        minHeight: 280, // Ensure card has minimum height
        cursor: 'pointer', // Show pointer cursor to indicate clickability
        // Background image styling
        ...(backgroundImage && {
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }),
        '&:hover': {
          boxShadow: 8,
          transform: 'translateY(-4px)', // More pronounced lift effect
          // Enhanced hover effect for cards with background images
          ...(backgroundImage && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.3)', // Much brighter overlay on hover - more inviting!
              zIndex: 1.5,
              transition: 'all 0.3s ease'
            }
          }),
          // For cards without background images, brighten the background
          ...(!backgroundImage && {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: backgroundImage ? getSalesColor() : 'primary.main'
          })
        }
      }}
      onClick={handleCardClick} // Make the entire card clickable
    >
      {/* Background overlay when there's a background image to ensure text readability */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.7) 100%)',
            zIndex: 1
          }}
        />
      )}

      {/* Sales Progress Bar */}
      <LinearProgress 
        variant="determinate" 
        value={status === 'not_on_sale' ? 0 : ticketSalesPercentage} 
        sx={{ 
          height: 4, 
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          position: 'relative',
          zIndex: 2,
          '& .MuiLinearProgress-bar': {
            backgroundColor: getSalesColor()
          }
        }} 
      />

      <CardContent sx={{ 
        p: 2, 
        pb: '16px !important',
        position: 'relative',
        zIndex: 2,
        // Adjust text colors when there's a background image
        ...(backgroundImage && {
          color: 'white',
          '& .MuiTypography-root': {
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
          }
        })
      }}>
        {/* Top section with band name, marketing indicator, and archive button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontSize: '1.25rem',
                fontWeight: 600,
                flex: 1,
                ...(backgroundImage && {
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
                })
              }}
            >
              {getBandName()}
            </Typography>
            
            {/* Marketing Assets Indicator */}
            {hasMarketingAssets && (
              <Tooltip title="Has marketing assets">
                <Badge
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: backgroundImage ? 'rgba(76, 175, 80, 1)' : 'success.main',
                      color: 'white',
                      border: backgroundImage ? '2px solid white' : 'none',
                      boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.4)' : 'none'
                    }
                  }}
                  badgeContent={<ImageIcon size={12} />}
                  color="success"
                >
                  <Box />
                </Badge>
              </Tooltip>
            )}
          </Box>
          
          <IconButton 
            size="small" 
            onClick={handleArchiveClick}
            sx={{ 
              ml: 1,
              ...(backgroundImage && {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                }
              })
            }}
          >
            {performance.isArchived ? <Undo size={18} /> : <Archive size={18} />}
          </IconButton>
        </Box>
        
        {/* Strand Info */}
        {getStrandName() && (
          <Typography 
            color={backgroundImage ? "inherit" : "text.secondary"} 
            variant="body2" 
            sx={{ 
              mb: 1,
              ...(backgroundImage && {
                color: 'rgba(255, 255, 255, 0.9)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              })
            }}
          >
            Strand: {getStrandName()}
          </Typography>
        )}

        {/* Venue Info */}
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1,
            ...(backgroundImage && {
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            })
          }}
        >
          Venue: {getVenueName()} {ticketCapacity > 0 && `(Capacity: ${ticketCapacity})`}
        </Typography>
        

<Box 
  sx={{ 
    display: 'flex', 
    alignItems: 'baseline', 
    gap: 1, 
    mb: 1.5 
  }}
>
  {/* Big day of the month */}
  <Typography 
    variant="h3"
    sx={{
      ...(backgroundImage && {
        color: 'white',
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
      })
    }}
  >
    {startDate.getDate()}
    {(() => {
      const day = startDate.getDate();
      const suffix = (day % 10 === 1 && day !== 11) ? 'st' :
                     (day % 10 === 2 && day !== 12) ? 'nd' :
                     (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
      return suffix;
    })()}
  </Typography>

  {/* Small time beside the date */}
  <Typography 
    variant="body2" 
    sx={{
      ...(backgroundImage && {
        color: 'white',
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
      })
    }}
  >
    {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
  </Typography>
</Box>
        
        {/* Status and Staff Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1.5 
        }}>

          
          {/* Staff Assignment */}
          <Tooltip title={`${staffCount}/${staffNeeded} staff assigned`}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AvatarGroup 
                max={3}
                sx={{ 
                  '& .MuiAvatar-root': { 
                    width: 28, 
                    height: 28, 
                    fontSize: '0.75rem',
                    ...(backgroundImage && {
                      border: '2px solid rgba(255, 255, 255, 0.3)'
                    })
                  } 
                }}
              >
                {performance.staff?.map((staffMember, index) => {
                  // Type guard to handle both object and string types for staffId
                  let initial = 'S';
                  
                  if (staffMember && typeof staffMember === 'object') {
                    // Access the staffId property
                    const staffId = staffMember.staffId;
                    
                    // Handle if staffId is an object and has a name property
                    if (
                      staffId &&
                      typeof staffId === 'object' &&
                      staffId !== null &&
                      typeof (staffId as { name?: string }).name === 'string'
                    ) {
                      // Now TypeScript knows staffId.name exists and is a string
                      initial = (staffId as { name: string }).name.charAt(0);
                    }
                  }
                  
                  return (
                    <Avatar 
                      key={index} 
                      sx={{ 
                        bgcolor: 'primary.main',
                        width: 28,
                        height: 28
                      }}
                    >
                      {initial}
                    </Avatar>
                  );
                })}
                {staffCount < staffNeeded && (
                  <Avatar 
                    sx={{ 
                      bgcolor: 'error.light',
                      width: 28,
                      height: 28
                    }}
                  >
                    +{staffNeeded - staffCount}
                  </Avatar>
                )}
              </AvatarGroup>
            </Box>
          </Tooltip>
        </Box>
        
        {/* Ticket Sales - Using our enhanced rendering function */}
        {renderTicketSales()}
        
        {/* Archived Info */}
        {performance.isArchived && performance.archiveDate && (
          <Typography 
            variant="caption" 
            display="block" 
            sx={{ 
              mt: 1, 
              color: backgroundImage ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary',
              ...(backgroundImage && {
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              })
            }}
          >
            Archived: {new Date(performance.archiveDate).toLocaleDateString()}
            {performance.archiveReason && ` - ${performance.archiveReason}`}
          </Typography>
        )}
      </CardContent>

      {/* Days Remaining - Bottom right corner */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          backgroundColor: backgroundImage ? 'rgba(25, 118, 210, 0.9)' : '#1976d2',
          color: 'white',
          borderRadius: '16px',
          padding: '4px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          zIndex: 3,
          boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 1,
          border: backgroundImage ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
        }}
      >
        <Typography 
          component="span"
          sx={{ 
            fontWeight: 'bold', 
            fontSize: '0.9rem',
            lineHeight: 1
          }}
        >
          {Math.abs(daysRemaining)}
        </Typography>
        <Typography 
          component="span"
          sx={{ 
            fontSize: '0.8rem',
            lineHeight: 1
          }}
        >
          {daysRemaining > 0 ? 'days' : daysRemaining < 0 ? 'days ago' : 'today'}
        </Typography>
      </Box>
    </Card>
  );
};

export default PerformanceCard;